const IMPERSONATION_TOKEN_KEY = 'impersonation_token';

const STORAGE_KEY = 'wave_impersonation';

export type ImpersonationSession = {
    staffEmail: string;
    creatorName: string;
};

type ImpersonationHandoff = {
    token: string;
    session: ImpersonationSession;
};

// JWT payloads are base64url with the padding stripped, which atob rejects outright.
const decodeBase64Url = (value: string): string => {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const bytes = Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
    return new TextDecoder().decode(bytes);
};

const readTextClaim = (value: unknown, fallback: string): string =>
    typeof value === 'string' && value !== '' ? value : fallback;

// Display-only read of the token's own claims. Nothing here is trusted: the backend
// re-validates the signature, expiry and creator scope on every request.
const readSessionFromToken = (token: string): ImpersonationSession | null => {
    const claims = token.split('.')[1];
    if (!claims) {
        return null;
    }

    try {
        const payload = JSON.parse(decodeBase64Url(claims)) as Record<string, unknown>;
        if (payload.imp !== true) {
            return null;
        }
        return {
            staffEmail: readTextClaim(payload.imp_by_email, 'a Wave admin'),
            creatorName: readTextClaim(payload.imp_creator_name, 'this creator'),
        };
    } catch {
        return null;
    }
};

/**
 * Reads an impersonation token out of the URL fragment and strips it back out, so the
 * credential does not linger in the address bar, browser history or a referrer header.
 * The fragment is never sent to a server, so the token also stays out of request logs.
 * Returns null when the URL carries no usable token.
 */
export const takeImpersonationTokenFromUrl = (): ImpersonationHandoff | null => {
    const url = new URL(window.location.href);
    const token = new URLSearchParams(url.hash.replace(/^#/, '')).get(IMPERSONATION_TOKEN_KEY);
    if (token === null) {
        return null;
    }

    url.hash = '';
    window.history.replaceState(null, '', url.toString());

    const session = readSessionFromToken(token);
    return session === null ? null : { token, session };
};

export const getImpersonationSession = (): ImpersonationSession | null => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === null) {
        return null;
    }

    try {
        return JSON.parse(stored) as ImpersonationSession;
    } catch {
        return null;
    }
};

export const setImpersonationSession = (session: ImpersonationSession): void => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
};

export const clearImpersonationSession = (): void => {
    localStorage.removeItem(STORAGE_KEY);
};
